import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { finalize, map, startWith, takeUntil } from 'rxjs/operators';
import { Frac } from '~lmo/models/frac.model';
import { Mesh } from '~lmo/models/mesh.model';
import { PurchaseOrder } from '~lmo/models/purchaseOrders.model';
import { LmoFracsService } from '~lmo/services';
import { LmoFracFcpoService } from '~lmo/services/lmo-frac-fcpo.service';
import { Mine } from '~models/mine.model';
import { User } from '~models/user';
import { UserApiService } from '~services/api/user.api.service';
import { ErrorHandlingService } from '~services/error-handling.service';
import { MineService } from '~services/mine.service';
import { UserService } from '~services/user.service';
import { trackById } from '~utilities/trackById';

interface EditPoFormValue {
  costPerTon: number;
  dailyMaxTon: number;
  dailyMinTon: number;
  id: number;
  penaltyCostPerTon: number;
  resetAllocationTime: Date;
  totalTonnage: number;
  permittedFracIds: number[];
  subscribedSandiUsers: number[];
}

@Component({
  selector: 'sa-lmo-create-purchase-order',
  styleUrls: ['./lmo-create-purchase-order.component.scss'],
  templateUrl: './lmo-create-purchase-order.component.html',
})
export class LmoCreatePurchaseOrderComponent implements OnInit, AfterViewInit, OnDestroy {
  public purchaseOrder$ = new BehaviorSubject<PurchaseOrder>(null);
  public mines$$ = new BehaviorSubject<Mine[]>([]);
  public meshes$$ = new BehaviorSubject<Mesh[]>([]);
  public fracs$: Observable<Frac[]>;
  public floor = Math.floor;
  public nonPoDataForm: FormGroup;
  public editPoForm: FormGroup;
  public newContractForm: FormControl;
  public updating: Boolean = false;
  public currentAccountId: number;
  public trackById = trackById;
  public filterMine = new FormControl();
  public filterUser: FormControl = new FormControl();
  public filteredUsers$: Observable<User[]>;
  private allPurchaseOrders: PurchaseOrder[] = [];
  @ViewChild('newPurchaseOrder', { static: true }) private newContractFormModal: TemplateRef<any>;
  @ViewChildren('newContractName') private newContractNameInput: QueryList<ElementRef>;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private newPoFormDialog: MatDialogRef<any>;
  private users$$ = new BehaviorSubject<User[]>([]);

  constructor(
    public dialogRef: MatDialogRef<LmoCreatePurchaseOrderComponent>,
    private snackBar: MatSnackBar,
    private fb: FormBuilder,
    private matDialog: MatDialog,
    private errorService: ErrorHandlingService,
    private userApiService: UserApiService,
    private userService: UserService,
    private fcpoService: LmoFracFcpoService,
    private fracService: LmoFracsService,
    private mineService: MineService,
  ) {
    this.currentAccountId = this.userService.accountId();

    this.userApiService
      .getWebUsers()
      .pipe(
        map((users) => {
          return users.sort((a, b) => a.name.localeCompare(b.name));
        }),
      )
      .subscribe((users) => {
        this.users$$.next(users);
      });

    this.filteredUsers$ = combineLatest([this.users$$, this.filterUser.valueChanges.pipe(startWith(''))]).pipe(
      map(([users, searchFilter]) => {
        if (!searchFilter) {
          return users;
        }

        const searchFilterValue = searchFilter.toLowerCase();
        return users.filter((option) => option.name.toLowerCase().indexOf(searchFilterValue) === 0);
      }),
      takeUntil(this.destroy$),
    );
  }

  public ngOnInit() {
    this.fcpoService.purchaseOrders$.pipe(takeUntil(this.destroy$)).subscribe((pos) => {
      this.allPurchaseOrders = pos.filter((po) => !po.closed);
    });

    this.nonPoDataForm = this.fb.group({
      meshId: this.fb.control({ value: null, disabled: true }, [Validators.required]),
      mineId: this.fb.control({ value: null }, [Validators.required]),
      purchaseOrder: this.fb.control({ value: null, disabled: true }, [Validators.required]),
    });

    combineLatest([
      this.filterMine.valueChanges.pipe(startWith('')),
      this.mineService.mines$.pipe(map((mines) => mines.data.sort((a, b) => (a.site.name > b.site.name ? 1 : -1)))),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([filterString, mines]) => {
        filterString = filterString + '';
        if (this.currentAccountId) {
          const finalMines = mines
            .filter((mine) => mine.site.ownerId === this.currentAccountId)
            .filter((mine) => mine.site.name.toLocaleLowerCase().indexOf(filterString.toLocaleLowerCase()) >= 0);
          this.mines$$.next(finalMines);
        }
      });

    combineLatest([
      this.nonPoDataForm.get('mineId').valueChanges,
      this.mineService.mines$.pipe(map((mines) => mines.data)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([mineId, mines]) => {
        if (mineId) {
          this.meshes$$.next(mines.find((mine) => mine.id === mineId).meshes);
          this.nonPoDataForm.get('purchaseOrder').disable({ onlySelf: true });
          this.nonPoDataForm.get('meshId').enable({ onlySelf: true });
          this.editPoForm.disable({ onlySelf: true });
        }
      });

    combineLatest([this.nonPoDataForm.get('meshId').valueChanges, this.nonPoDataForm.get('mineId').valueChanges]).pipe(
      map(([meshId, mineId]: [string, string]) => {
        this.editPoForm.disable({ onlySelf: true });
        if (meshId && mineId) {
          return this.allPurchaseOrders.filter((po) => po.meshId === +meshId && po.mineId === +mineId);
        }
        return [];
      }),
      map((POs) => {
        return POs.sort((a, b) => (a.allocation - (a.ordered || 0) < b.allocation - (b.ordered || 0) ? 1 : -1));
      }),
      takeUntil(this.destroy$),
    );

    this.nonPoDataForm.get('meshId').valueChanges.subscribe((meshId) => {
      this.editPoForm.disable({ onlySelf: true });
      this.nonPoDataForm.get('purchaseOrder').reset();
      this.fracs$ = this.fracService.fracs$.pipe(
        map((fracs) => fracs.filter((frac) => this.hasSelectedMeshId(frac, meshId))),
      );
    });

    this.newContractForm = this.fb.control(null, [Validators.required]);

    this.setupEditPurchaseOrderForm();

    this.nonPoDataForm.get('purchaseOrder').valueChanges.subscribe((data) => {
      if (data) {
        this.editPoForm.enable({ onlySelf: true });
        const po = this.purchaseOrder$.value;
        if (po.id > 0) {
          this.editPoForm.get('costPerTon').setValue(po.costPerTon);
          this.editPoForm.get('dailyMaxTon').setValue(po.maximumDailyAllocation / 2000);
          this.editPoForm.get('dailyMinTon').setValue(po.minimumDailyAllocation / 2000);
          this.editPoForm.get('penaltyCostPerTon').setValue(po.penaltyCostPerTon);
          this.editPoForm.get('totalTonnage').setValue(po.allocation / 2000);
          const displayTime = new Date(
            moment()
              .startOf('day')
              .add(po.resetAllocationTimeOffset, 'minute')
              .unix() * 1000,
          );
          this.editPoForm.get('resetAllocationTime').setValue(displayTime);
          this.editPoForm.get('permittedFracIds').setValue(po.permittedFracIds);
          this.editPoForm.get('subscribedSandiUsers').setValue(po.subscribedUserIds);
        }
      }
    });
  }

  public startNewPo() {
    this.newPoFormDialog = this.matDialog.open(this.newContractFormModal, {
      maxWidth: '500px;',
    });
  }

  public createPo() {
    const name = this.newContractForm.value;
    const mineId = this.nonPoDataForm.get('mineId').value;
    const meshId = this.nonPoDataForm.get('meshId').value;
    if (!name || !mineId || !meshId) {
      return;
    }
    this.fcpoService.createPurchaseOrder$(name, mineId, meshId).subscribe(
      (po) => {
        if (po) {
          this.purchaseOrder$.next(po);
          this.nonPoDataForm.get('purchaseOrder').setValue(po.name);
          if (this.newPoFormDialog) {
            this.newPoFormDialog.close();
            this.newPoFormDialog = null;
          }
        }
      },
      (error) => {
        this.errorService.showError(error);
      },
    );
  }

  public ngAfterViewInit() {
    this.newContractNameInput.changes.subscribe((input) => {
      if (input.length > 0) {
        input.first.nativeElement.focus();
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    // Just in case
    this.destroy$.unsubscribe();
  }

  public updatePo() {
    const formValue = this.editPoForm.value as EditPoFormValue;

    const pickedValue = moment(formValue.resetAllocationTime);
    const startOfDay = pickedValue.clone().startOf('day');
    const minutesOffSet = pickedValue.diff(startOfDay, 'minutes');

    const po = this.purchaseOrder$.value;
    const body = {
      allocation: formValue.totalTonnage * 2000,
      costPerTon: formValue.costPerTon,
      id: po.id,
      maximumDailyAllocation: (formValue.dailyMaxTon || 0) * 2000,
      meshId: this.nonPoDataForm.get('meshId').value,
      mineId: this.nonPoDataForm.get('mineId').value,
      minimumDailyAllocation: (formValue.dailyMinTon || 0) * 2000,
      name: this.nonPoDataForm.get('purchaseOrder').value,
      ownerId: this.currentAccountId,
      penaltyCostPerTon: formValue.penaltyCostPerTon,
      permittedFracIds: formValue.permittedFracIds,
      resetAllocationTimeOffset: minutesOffSet,
      subscribedUserIds: formValue.subscribedSandiUsers,
    };

    this.updating = true;
    this.fcpoService
      .updatePurchaseOrder$(po.id, body)
      .pipe(
        finalize(() => {
          this.updating = false;
        }),
      )
      .subscribe(
        (successful) => {
          if (successful) {
            this.snackBar.open(`Purchase Order is updated`, null, {
              duration: 2500,
            });
            this.dialogRef.close(po.id);
          }
        },
        () => {
          this.snackBar.open('Something Went Wrong', null, {
            duration: 2500,
            panelClass: ['snackbar-error'],
          });
        },
      );
  }

  public closeTab() {
    this.newPoFormDialog.close();
    this.newPoFormDialog = null;
  }

  public calculatePoRemaining(po: PurchaseOrder): number {
    const remaining = po.allocation - (po.pickedUp || 0);
    if (remaining < 0) {
      return 0;
    }
    return remaining;
  }

  private setupEditPurchaseOrderForm() {
    this.editPoForm = this.fb.group({
      costPerTon: [null, [Validators.required, Validators.min(0)]],
      dailyMaxTon: [null, [Validators.required, Validators.min(0)]],
      dailyMinTon: [null, [Validators.required, Validators.min(0)]],
      penaltyCostPerTon: [null, [Validators.required, Validators.min(0)]],
      permittedFracIds: [null],
      resetAllocationTime: [null, [Validators.required]],
      subscribedSandiUsers: [null],
      totalTonnage: [null, [Validators.required, Validators.min(0)]],
    });
    this.editPoForm.disable();
  }

  private hasSelectedMeshId(frac: Frac, meshId: number): boolean {
    return frac.fracStorages.some((storage) => storage.mesh.id === meshId);
  }
}
